import { Injectable } from '@angular/core';
import { Subject, type Observable } from 'rxjs';
import type { BusEvent } from './bus-event';

/**
 * Root-singleton dispatcher backing the typed, area-scoped event bus.
 *
 * Holds a single, ordered, immortal `Subject<BusEvent>`. The subject is never
 * `error`/`complete`d — a terminated subject would silently drop every future
 * event for the app's lifetime. Each app gets its own instance (root-provided);
 * runtime is per-app, code is shared via `@tvd/cdk/events`.
 *
 * Area facades (subclasses of `AreaEvents`) are the only intended callers; they
 * push through `dispatch` and read the ordered stream via `events$`.
 */
@Injectable({ providedIn: 'root' })
export class EventDispatcher {
  readonly #subject = new Subject<BusEvent>();

  /** The single ordered stream of every event dispatched in this app. */
  readonly events$: Observable<BusEvent> = this.#subject.asObservable();

  /** Push an event onto the shared ordered stream. */
  dispatch(event: BusEvent): void {
    this.#subject.next(event);
  }
}
